<template>
  <v-dialog v-model="internalValue" max-width="600" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Set Activation State</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="device" class="mb-4">
          <div class="text-subtitle-1 font-weight-medium">
            {{ device.customName || 'Unnamed Device' }}
          </div>
          <div class="text-caption grey--text">
            ID: {{ device.id }} | Company: {{ device.companyName }}
          </div>
          <div class="text-caption grey--text">
            Current Activation State:
            {{
              device.activationState ? device.activationState.replace('_', ' ') : 'Billing disabled'
            }}
          </div>
        </div>

        <!-- Activation State Selection -->
        <v-select
          v-model="form.activationState"
          :items="activationStateOptions"
          label="Activation State"
          hint="Select the desired activation state. Billing disabled turns billing off for the device."
          persistent-hint
          class="mb-4"
        />

        <!-- Dialog Type Selection: only relevant when turning billing on (disabled -> Introductory period) -->
        <v-select
          v-if="showDialogTypeSelect"
          v-model="form.dialogType"
          :items="dialogTypeOptions"
          label="Dialog Type"
          hint="Which subscription dialog / push notification to send to the device admin."
          persistent-hint
          class="mb-4"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close"> Cancel </v-btn>
        <v-btn color="primary" :loading="loading" @click="setActivationState"> Save </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { adminUpdateSubscriptionActivationState } from '@/graphql/mutations/adminUpdateSubscriptionActivationState'

export default {
  name: 'SetBillingActivationStateModal',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    device: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      form: {
        activationState: null,
        dialogType: 'M2M_ACTIVATION',
      },

      activationStateOptions: [
        { text: 'Introductory period', value: 'INTRODUCTORY_PERIOD' },
        { text: 'Accepted', value: 'ACCEPTED' },
        { text: 'Active', value: 'ACTIVE' },
        { text: 'Declined', value: 'DECLINED' },
        { text: 'Free', value: 'FREE' },
        { text: 'Billing disabled', value: null },
      ],

      dialogTypeOptions: [
        { text: 'Standard activation', value: 'M2M_ACTIVATION' },
        { text: 'Backup activation', value: 'M2M_BACKUP_ACTIVATION' },
      ],
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },

    // The dialog type only matters when billing is being turned on from a disabled state -
    // that's the only path that sends a fresh activation dialog/push notification to the admin.
    showDialogTypeSelect() {
      return !this.device?.activationState && this.form.activationState === 'INTRODUCTORY_PERIOD'
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.form.activationState = this.device ? this.device.activationState : null
        this.form.dialogType = 'M2M_ACTIVATION'
      }
    },
  },

  methods: {
    close() {
      this.internalValue = false
    },

    async setActivationState() {
      this.loading = true

      try {
        const input = {
          deviceId: this.device.id,
          activationState: this.form.activationState,
        }
        // Omitted otherwise so the backend keeps using its default (M2M_ACTIVATION).
        if (this.showDialogTypeSelect) {
          input.dialogType = this.form.dialogType
        }

        const { data } = await this.$apollo.mutate({
          mutation: adminUpdateSubscriptionActivationState,
          variables: { input },
        })

        if (data.adminUpdateSubscriptionActivationState.success) {
          this.$toast.success('Activation state updated successfully')
          this.$emit('success')
        } else {
          this.$toast.error(
            data.adminUpdateSubscriptionActivationState.message ||
              'Failed to update activation state',
          )
        }
      } catch (error) {
        console.error('Error setting activation state:', error)
        this.$toast.error('Error setting activation state: ' + error.message)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
